import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import AiFoundation from './view/AIview';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { AzureContextProvider, useAzureData } from './context/AzureContext';
import MaintenancePage from './components/MaintenancePage';
import { FallbackProps, ErrorBoundary } from 'react-error-boundary';
import UnileverAnimation from './components/UnileverAnimation';

// error Boundary
const Fallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
};

function App() {
  const isMaintenance = process.env.REACT_APP_MAINTENANCE === 'true';
  if (isMaintenance) {
    return <MaintenancePage />;
  }

  const RequireAdmin = ({ children }: { children: JSX.Element }) => {
    const { isAdmin } = useAzureData();
    if (isAdmin === null) {
      return (
        <div className="w-100 vh-100 d-flex justify-content-center align-items-center">
          <UnileverAnimation />
        </div>
      );
    }
    if (!isAdmin) {
      return <Navigate to="/myapps" replace />;
    }
    return children;
  };

  const RestrictedPath = ({
    children,
    allowedRoles
  }: {
    children: JSX.Element;
    allowedRoles: { admin?: boolean; owner?: boolean; appUser?: boolean; ragUser?: boolean };
  }) => {
    const { tempAdmin, tempOwner, appUser, ragUser } = useAzureData();
    if (tempAdmin == null || tempOwner === null) {
      return (
        <div className="w-100 vh-100 d-flex justify-content-center align-items-center">
          <UnileverAnimation />
        </div>
      );
    }

    if (
      (allowedRoles.admin && tempAdmin) ||
      (allowedRoles?.owner && tempOwner) ||
      (allowedRoles?.appUser && appUser) ||
      (allowedRoles?.ragUser && ragUser)
    ) {
      return children;
    }

    return <Navigate to="/myapps" replace />;
  };

  return (
    <ErrorBoundary FallbackComponent={Fallback} onReset={() => window.location.reload()}>
      <AzureContextProvider>
        <AuthenticatedTemplate>
          <Routes>
            {/*limited accesss to  addmins only  */}
            <Route path="/dashboard" element={<AiFoundation page="dashboard" />} />
            <Route path="/leaderboard" element={<AiFoundation page="leaderboard" />} />
            <Route path="/finops" element={<AiFoundation page="finops" />} />

            {/* App Layer */}
            {/* Contitionally Available */}
            <Route path="/" element={<AiFoundation page="myapps" />} />
            <Route path="/myapps" element={<AiFoundation page="myapps" />} />
            <Route path="/onboarding" element={<AiFoundation page="onboarding" />} />

            <Route
              path="/manageapprequests"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: false, appUser: false, ragUser: false }}>
                  <AiFoundation page="manageapprequests" />
                </RestrictedPath>
              }
            />

            {/* Model Layer*/}
            {/* Contitionally available */}
            <Route
              path="/model"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: false, appUser: false, ragUser: false }}>
                  <AiFoundation page="model" />
                </RestrictedPath>
              }
            />
            <Route
              path="/modelOnboarding"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: false, appUser: false, ragUser: false }}>
                  <AiFoundation page="modelOnboarding" />
                </RestrictedPath>
              }
            />
            {/* <Route path="/model" element={<AiFoundation page="modelOnboarding" />} /> */}

            {/* RAG Layer */}
            {/* Open for all Roles ie. Admin , Owner  , AppUser , Playground User  */}
            <Route path="/managerag" element={<AiFoundation page="managerag" />} />
            <Route path="/newragform" element={<AiFoundation page="newragform" />} />
            <Route path="/ragbuilder" element={<AiFoundation page="ragbuilder" />} />

            {/* System layer */}
            {/* Conditionally available */}
            <Route
              path="/systemanalytics"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: true, appUser: true, ragUser: false }}>
                  <AiFoundation page="systemanalytics" />
                </RestrictedPath>
              }
            />
            <Route
              path="/ethicscompliance"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: true, appUser: true, ragUser: false }}>
                  <AiFoundation page="ethicscompliance" />
                </RestrictedPath>
              }
            />
            <Route
              path="/healthdashboard"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: true, appUser: false, ragUser: false }}>
                  <AiFoundation page="healthdashboard" />
                </RestrictedPath>
              }
            />
            <Route
              path="/costAnalytics"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: true, appUser: true, ragUser: false }}>
                  <AiFoundation page="costAnalytics" />
                </RestrictedPath>
              }
            />
            <Route
              path="/unifiedanalytics"
              element={
                <RestrictedPath
                  allowedRoles={{ admin: true, owner: true, appUser: true, ragUser: false }}>
                  <AiFoundation page="unifiedanalytics" />
                </RestrictedPath>
              }
            />
            <Route path="/rai" element={<AiFoundation page="rai" />} />

            {/* Prompt Layer  */}
            {/* Open for all Roles ie. Admin , Owner  , AppUser , Playground User  */}
            <Route path="/comparePrompts" element={<AiFoundation page="comparePrompts" />} />
            <Route path="/addPrompt" element={<AiFoundation page="addPrompt" />} />
            <Route path="/promptlibrary" element={<AiFoundation page="promptlibrary" />} />
            <Route path="/promptplayground" element={<AiFoundation page="promptplayground" />} />
          </Routes>
        </AuthenticatedTemplate>
      </AzureContextProvider>
    </ErrorBoundary>
  );
}

export default App;
